import { useEffect, useState } from "react";
import { Upload, Plus, Cpu } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { EnginePicker } from "./EnginePicker";
import { confirmPaidRun } from "@/components/engines/paid";

const STEMS = [["effects", "Effects"], ["music", "Music"], ["dialogue", "Dialogue"], ["me", "M&E"]];

// Bring stems separated elsewhere (RunPod, local GPU, vendor export) into this benchmark, or queue more engines on the same master.
export const ImportStems = ({ benchmark, onDone }) => {
  const [engines, setEngines] = useState([]);
  const [engine, setEngine] = useState("mvsep_cinematic_local");
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [secs, setSecs] = useState("");
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [extra, setExtra] = useState([]);
  const [extraOptions, setExtraOptions] = useState({});
  useEffect(() => { api.get("/engines").then((r) => setEngines(r.data.engines)); }, []);
  const picked = Object.values(files).filter(Boolean).length;

  const submit = async () => {
    const fd = new FormData();
    fd.append("engine", engine); fd.append("label", label); fd.append("notes", notes);
    if (secs) fd.append("processing_duration_sec", secs);
    Object.entries(files).forEach(([s, f]) => f && fd.append(s, f));
    setBusy(true);
    try {
      await api.post(`/benchmark/${benchmark.id}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${picked} stem(s) as ${label || engine}`);
      setFiles({}); setLabel(""); setNotes(""); setSecs("");
      onDone?.();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const runMore = async () => {
    if (!confirmPaidRun(engines, extra)) return;
    setBusy(true);
    try {
      await api.post(`/benchmark/${benchmark.id}/run`, { engines: extra, engine_options: extraOptions });
      toast.success(`Queued ${extra.length} engine run(s)`);
      setExtra([]);
      onDone?.();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const worker = engines.find((e) => e.key === "mvsep_cinematic_local");
  const productionRun = async () => {
    if (!window.confirm("Queue this master on the MVSEP GPU worker at Fast and HQ (DX/MX/FX/ME each) and terminate the pod when both finish?")) return;
    setBusy(true);
    try {
      const r = await api.post("/gpu-test/production-run", { benchmark_id: benchmark.id, qualities: ["fast", "hq"], terminate_when_done: true });
      toast.success(`Queued ${r.data.job_ids.length} GPU runs · pod will auto-terminate when done`);
      onDone?.();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const input = "mt-1 w-full bg-[#0B0E14] border border-[#253044] rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-cyan-500";
  return (
    <div data-testid="benchmark-import-panel" className="panel p-4 grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-3">
        <div className="micro flex items-center gap-2"><Upload size={13} /> Import externally processed stems</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block"><span className="micro">Engine / source</span>
            <select data-testid="import-engine-select" value={engine} onChange={(e) => setEngine(e.target.value)} className={input}>
              {engines.map((e) => <option key={e.key} value={e.key}>{e.name}</option>)}
            </select></label>
          <label className="block"><span className="micro">Label (e.g. RunPod L40S · HQ)</span>
            <input data-testid="import-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Low Q / HQ / v2 …" className={input} /></label>
          <label className="block"><span className="micro">Processing time (s, optional)</span>
            <input data-testid="import-secs-input" type="number" min="0" value={secs} onChange={(e) => setSecs(e.target.value)} className={input} /></label>
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          {STEMS.map(([k, l]) => (
            <label key={k} className={`block rounded-sm border p-2 cursor-pointer transition-colors ${files[k] ? "border-cyan-500 bg-cyan-500/10" : "border-[#253044] hover:bg-[#1E2738]"}`}>
              <span className="micro">{l} stem</span>
              <input data-testid={`import-file-${k}`} type="file" accept="audio/*,.wav,.flac,.aif,.aiff,.mp3" onChange={(e) => setFiles((f) => ({ ...f, [k]: e.target.files?.[0] || null }))} className="mt-1 block w-full text-[11px] text-slate-400 file:mr-2 file:px-2 file:py-1 file:rounded-sm file:border-0 file:bg-[#1E2738] file:text-slate-200" />
            </label>
          ))}
        </div>
        <input data-testid="import-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes: model, settings, GPU, shifts/overlap …" className={input} />
        <button data-testid="import-submit-button" disabled={busy || !picked} onClick={submit}
          className="flex items-center gap-2 px-4 h-9 rounded-sm bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold disabled:opacity-40">
          <Upload size={14} /> Import {picked || ""} stem{picked === 1 ? "" : "s"} into benchmark
        </button>
        <p className="text-[11px] text-slate-500">Music + Effects without M&E → M&E is derived by summing. Imported stems get the same 24-bit normalisation, waveform + leakage heatmap as engine runs.</p>
      </div>
      <div className="space-y-3 lg:border-l lg:border-[#1c2433] lg:pl-6">
        <div className="micro flex items-center gap-2"><Plus size={13} /> Run more engines on this master</div>
        <EnginePicker engines={engines} picked={extra} setPicked={setExtra} options={extraOptions} setOptions={setExtraOptions} columns="grid-cols-1" testPrefix="run-more-engine" />
        <button data-testid="run-more-submit-button" disabled={busy || !extra.length} onClick={runMore}
          className="w-full h-9 rounded-sm bg-[#E11D48] hover:bg-[#be123c] text-white text-sm font-semibold disabled:opacity-40">Queue {extra.length || ""} run(s)</button>
        <div className="border-t border-[#1c2433] pt-3 space-y-2">
          <div className="micro flex items-center gap-2"><Cpu size={13} /> Production run on GPU worker</div>
          <button data-testid="production-run-button" disabled={busy || !worker?.configured} onClick={productionRun} title={worker?.configured ? "" : "Connect the MVSEP worker on the Engines page first"}
            className="w-full h-9 rounded-sm bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold disabled:opacity-40">MVSEP Fast + HQ → auto-terminate pod</button>
          <p className="text-[11px] text-slate-500">{worker?.configured ? `Worker: ${worker.detail}` : "GPU worker not connected."} Two DME runs (DX / MX / FX / ME), then the RunPod pod is terminated automatically.</p>
        </div>
      </div>
    </div>
  );
};
